import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';

interface IProps {
  parentElement: string;
  selectors: string[];
  children: ReactNode;
}

const FocusLoop = observer((props: IProps): ReactElement => {
  const firstElementRef = useRef<HTMLElement | null>(null);
  const lastElementRef = useRef<HTMLElement | null>(null);
  const isFirstSelectionRef = useRef<boolean>(true);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    findElements();
    isFirstSelectionRef.current = true;
  }, [props.parentElement, props.selectors, props.children]);

  const findElements = (): void => {
    const node = rootRef.current;
    const parentElements = node?.querySelectorAll(`.${props.parentElement}`) ?? [];

    if (parentElements[0] instanceof HTMLElement) {
      for (let i = 0; i < props.selectors.length; i += 1) {
        const firstElement = parentElements[0].querySelector(props.selectors[i]);
        if (firstElement !== null) {
          firstElementRef.current = firstElement as HTMLElement;
          break;
        }
      }
    }

    if (parentElements[parentElements.length - 1] instanceof HTMLElement) {
      for (let i = 0; i < props.selectors.length; i += 1) {
        const lastElement = parentElements[parentElements.length - 1].querySelector(props.selectors[i]);
        if (lastElement !== null) {
          lastElementRef.current = lastElement as HTMLElement;
          break;
        }
      }
    }
  };

  const handleFocusUpperSpan = (): void => {
    if (isFirstSelectionRef.current) {
      isFirstSelectionRef.current = false;
      focusFirstElement();
      return;
    }

    focusLastElement();
  };

  const focusFirstElement = (): void => {
    firstElementRef.current?.focus();
  };

  const focusLastElement = (): void => {
    lastElementRef.current?.focus();
  };

  return (
    <div ref={rootRef}>
      <span
        tabIndex={0}
        onFocus={handleFocusUpperSpan}
      />
      {props.children}
      <span
        tabIndex={0}
        onFocus={focusFirstElement}
      />
    </div>
  );
});

export { FocusLoop };
